import { Link } from '@tanstack/react-router'
import { parseAsString, useQueryState } from 'nuqs'
import { FlaskConical } from 'lucide-react'
import { useCurrentUser } from '@/api/hooks'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useMyProgress } from './api'
import { TRACKS, lessonByKey, trackForRole } from './content'
import { LearnHeader } from './learn-nav'
import { LessonStatusIcon, QuizScore } from './lesson-status'
import { lessonState } from './lesson-state'

export function LearnPage() {
  const me = useCurrentUser()
  const progress = useMyProgress()
  const suggested = trackForRole(me.data?.role, me.data?.store_chat_responder)
  const [trackParam, setTrack] = useQueryState('track', parseAsString)
  const track = TRACKS.find((t) => t.key === (trackParam ?? suggested)) ?? TRACKS[0]!
  const lessons = track.lessons.map(lessonByKey).filter((lesson) => lesson !== undefined)

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <LearnHeader view="lessons" />

      <div role="tablist" aria-label="Tracks" className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
        {TRACKS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === track.key}
            onClick={() => void setTrack(t.key === suggested ? null : t.key)}
            className={cn(
              'shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              t.key === track.key
                ? 'border-foreground bg-foreground text-background'
                : 'bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {t.title}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border bg-background">
        {lessons.map((lesson) => {
          const row = progress.data?.get(lesson.key)
          return (
            <Link
              key={lesson.key}
              to="/learn/$lessonKey"
              params={{ lessonKey: lesson.key }}
              className="flex items-start gap-3 border-b px-4 py-3.5 transition-colors last:border-b-0 hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
            >
              {progress.isLoading ? <Skeleton className="size-5 rounded-full" /> : <LessonStatusIcon state={lessonState(row)} />}
              <div className="min-w-0 flex-1">
                <div className="font-medium">{lesson.title}</div>
                <div className="text-sm text-muted-foreground">{lesson.summary}</div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-3">
                <QuizScore score={row?.quiz_score} />
                {lesson.practice && (
                  <span className="inline-flex items-center gap-1">
                    <FlaskConical className="size-3.5" aria-hidden="true" />
                    Practice
                  </span>
                )}
                <span className="tabular-nums">{lesson.minutes} min</span>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
